'use client';

/**
 * SecureTransactionBadge Component
 *
 * Displays security assurance message with payment method logos.
 * Used in shopping bag and checkout pages to build trust.
 *
 * Features:
 * - Lock icon with "ALL TRANSACTIONS ARE SAFE AND SECURE" text
 * - Payment method logos fetched dynamically from Adyen
 * - Brand-aware styling via CSS tokens
 */

import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';

import { PaymentMethodsLogo } from '@/components/checkout/PaymentMethodsLogo';
import { BRAND_NAME } from '@/lib/const';

import styles from './SecureTransactionBadge.module.scss';

export interface SecureTransactionBadgeProps {
  className?: string;
  paymentLogos?: ReactNode;
}

export function SecureTransactionBadge({
  className,
  paymentLogos,
}: Readonly<SecureTransactionBadgeProps>) {
  const t = useTranslations('checkout');

  return (
    <div className={clsx(styles.container, className)}>
      <div className={styles.header}>
        <span className={styles.text}>{t('cart.secureTransactions')}</span>
      </div>
      {paymentLogos === undefined ? <PaymentMethodsLogo className={styles.logos} /> : paymentLogos}
      <p className={styles.notice}>{t('cart.encryptedPaymentNotice', { brand: BRAND_NAME })}</p>
    </div>
  );
}
